import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { X, Search, Plus, Building2, Check, User } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.VITE_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function ClientSelector({ isOpen, onClose, onSelectClient, language }) {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newClient, setNewClient] = useState({
    name: "", legal_form: "SAS", rcs: "Paris", siren: "", capital: "",
    address: "", representative_name: "", representative_title: "Directeur Général", designation: ""
  });

  useEffect(() => {
    if (isOpen) fetchClients();
  }, [isOpen]);

  const fetchClients = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('lawliet_token');
      const response = await axios.get(`${API}/clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClients(response.data.clients || []);
    } catch (error) {
      console.error("Error fetching clients:", error);
      if (error.response?.status && error.response.status !== 200) {
        toast.error(language === "fr" ? "Erreur chargement clients" : "Error loading clients");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateClient = async () => {
    if (!newClient.name || !newClient.siren || !newClient.representative_name) {
      toast.error(language === "fr" ? "Remplissez les champs obligatoires" : "Fill required fields");
      return;
    }
    try {
      const token = localStorage.getItem('lawliet_token');
      await axios.post(`${API}/clients`, newClient, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(language === "fr" ? "Client créé !" : "Client created!");
      setShowCreateForm(false);
      fetchClients();
      setNewClient({ name: "", legal_form: "SAS", rcs: "Paris", siren: "", capital: "", address: "", representative_name: "", representative_title: "Directeur Général", designation: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
    }
  };

  const filteredClients = clients.filter(c => 
    c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.siren.includes(searchQuery) ||
    c.representative_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}} onClick={onClose} className="fixed inset-0 bg-black/50 z-50 backdrop-blur-sm" />
          <motion.div initial={{opacity:0,scale:0.95}} animate={{opacity:1,scale:1}} exit={{opacity:0,scale:0.95}} className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col">
              <div className="p-6 border-b flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold">{language === "fr" ? "Sélectionner un client" : "Select a client"}</h2>
                  <p className="text-sm text-slate-600 mt-1">{language === "fr" ? "Ou créez-en un nouveau" : "Or create new"}</p>
                </div>
                <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg"><X className="h-5 w-5" /></button>
              </div>
              {!showCreateForm ? (
                <>
                  <div className="p-6 pb-4 border-b">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Rechercher..." className="pl-10" />
                    </div>
                  </div>
                  <div className="flex-1 overflow-y-auto p-6">
                    {loading ? <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div> :
                    filteredClients.length === 0 ? <div className="text-center py-12"><Building2 className="h-12 w-12 mx-auto mb-3 opacity-50" /><p className="text-sm text-slate-500">Aucun client</p></div> :
                    <div className="space-y-2">
                      {filteredClients.map(c => (
                        <button key={c.id} onClick={() => {onSelectClient(c);onClose();}} className="w-full text-left p-4 border-2 border-slate-200 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition group">
                          <div className="flex items-center gap-2 mb-1"><Building2 className="h-4 w-4 text-blue-600" /><h3 className="font-semibold">{c.name}</h3><Badge variant="outline" className="text-xs">{c.legal_form}</Badge></div>
                          <p className="text-xs text-slate-600">SIREN: {c.siren} • RCS {c.rcs}</p>
                          <p className="text-xs text-slate-600"><User className="h-3 w-3 inline" /> {c.representative_name} ({c.representative_title})</p>
                        </button>
                      ))}
                    </div>}
                  </div>
                  <div className="p-6 border-t bg-slate-50 flex justify-between">
                    <p className="text-sm text-slate-600">{filteredClients.length} client(s)</p>
                    <Button onClick={() => setShowCreateForm(true)} className="gap-2"><Plus className="h-4 w-4" />Nouveau client</Button>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex-1 overflow-y-auto p-6 space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="col-span-2"><Label>Raison sociale *</Label><Input value={newClient.name} onChange={(e) => setNewClient({...newClient, name: e.target.value})} /></div>
                      <div><Label>Forme juridique *</Label><select value={newClient.legal_form} onChange={(e) => setNewClient({...newClient, legal_form: e.target.value})} className="w-full px-3 py-2 border rounded-lg"><option value="SAS">SAS</option><option value="SARL">SARL</option><option value="SA">SA</option><option value="SCI">SCI</option><option value="EURL">EURL</option></select></div>
                      <div><Label>Capital (€) *</Label><Input value={newClient.capital} onChange={(e) => setNewClient({...newClient, capital: e.target.value})} placeholder="100000" /></div>
                      <div><Label>SIREN *</Label><Input value={newClient.siren} onChange={(e) => setNewClient({...newClient, siren: e.target.value})} placeholder="123456789" maxLength={9} /></div>
                      <div><Label>RCS *</Label><Input value={newClient.rcs} onChange={(e) => setNewClient({...newClient, rcs: e.target.value})} placeholder="Paris" /></div>
                      <div className="col-span-2"><Label>Adresse siège *</Label><Input value={newClient.address} onChange={(e) => setNewClient({...newClient, address: e.target.value})} placeholder="10 Avenue..." /></div>
                      <div><Label>Représentant *</Label><Input value={newClient.representative_name} onChange={(e) => setNewClient({...newClient, representative_name: e.target.value})} /></div>
                      <div><Label>Fonction *</Label><Input value={newClient.representative_title} onChange={(e) => setNewClient({...newClient, representative_title: e.target.value})} placeholder="Directeur Général" /></div>
                      <div className="col-span-2"><Label>Désignation</Label><Input value={newClient.designation} onChange={(e) => setNewClient({...newClient, designation: e.target.value})} placeholder="la Société, le Client..." /></div>
                    </div>
                  </div>
                  <div className="p-6 border-t flex justify-end gap-3">
                    <Button variant="outline" onClick={() => setShowCreateForm(false)}>Retour</Button>
                    <Button onClick={handleCreateClient}>Créer client</Button>
                  </div>
                </>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
